import {useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Practitioner referrals (2026-10-30). Who referred whom, what it brought in,
// and the rewards waiting for approval. Nothing is given until it's approved
// here. Backend: /referrals/admin/* (enforma-backend/helpers/referral.helper.js).

const day = (d) => d.toISOString().slice(0, 10)
const daysAgo = (n) => day(new Date(Date.now() - n * 864e5))
const RANGES = {
  all: {label: 'All time', from: () => '', to: () => ''},
  '30': {label: 'Referred in the last 30 days', from: () => daysAgo(29), to: () => daysAgo(0)},
  '90': {label: 'Referred in the last 90 days', from: () => daysAgo(89), to: () => daysAgo(0)},
  year: {label: 'Referred this year', from: () => daysAgo(0).slice(0, 4) + '-01-01', to: () => daysAgo(0)},
}
const TOWNS = {
  Sayulita_Nay: 'Sayulita',
  San_Miguel_De_Allende: 'San Miguel de Allende',
  'Puerto Escondido': 'Puerto Escondido',
  Tulum: 'Tulum',
}
const townLabel = (v) => TOWNS[v] || v
const REWARDS = {
  spotlight: 'Instagram spotlight',
  premium_month: '1 free Premium month',
  top_spot: 'Top spot in their category for a month',
}
const HOW = {
  spotlight: 'Post them on EnForma’s Instagram, then click Given.',
  premium_month: 'Given straight away: a 30-day Premium grant (added to any grant they already have).',
  top_spot: 'Move them to first place in Listing Order for a month, then click Given.',
}

const n = (v) => (v || 0).toLocaleString('en-US')
const money = (cents) => {
  const parts = Object.entries(cents || {})
    .filter(([, v]) => v)
    .map(([cur, v]) => `${cur} ${(v / 100).toLocaleString('en-US', {maximumFractionDigits: 0})}`)
  return parts.length ? parts.join(' · ') : '–'
}
const usd = (cents) => (cents ? `USD ${(cents / 100).toLocaleString('en-US', {maximumFractionDigits: 0})}` : '–')
const date = (d) => (d ? new Date(d).toLocaleDateString('en-US', {day: 'numeric', month: 'short', year: 'numeric'}) : '')

const why = (w) => {
  const who = w.referred?.name || 'a practitioner'
  if (w.kind === 'practitioner_live') return `Invited ${who}, whose profile is now live`
  if (w.kind === 'practitioner_paid') return `Invited ${who}, who now pays for Premium`
  return `5 invited clients made a paid booking (group ${w.seq})`
}

const COLUMNS = [
  {key: 'referred', label: 'Referred', get: (p) => p.referred},
  {key: 'practitioners', label: 'Practitioners', get: (p) => p.practitioners},
  {key: 'live', label: 'Went live', get: (p) => p.live},
  {key: 'paidPremium', label: 'Pay Premium', get: (p) => p.paidPremium},
  {key: 'clients', label: 'Clients', get: (p) => p.clients},
  {key: 'bookingClients', label: 'Clients who booked', get: (p) => p.bookingClients, hint: 'Paid bookings that have taken place'},
  {key: 'bookings', label: 'Paid bookings', get: (p) => p.bookings},
  {key: 'fee', label: 'EnForma fees', get: (p) => Object.values(p.feeCents).reduce((s, v) => s + v, 0), show: (p) => money(p.feeCents)},
  {key: 'premium', label: 'Premium revenue', get: (p) => p.premiumUsdCents, show: (p) => usd(p.premiumUsdCents), hint: 'Charged by Apple/Google (before their cut), from Oct 30 2026'},
  {key: 'rewardsGiven', label: 'Rewards given', get: (p) => p.rewardsGiven, show: (p) => `${n(p.rewardsGiven)}${p.rewardsPending ? ` (+${p.rewardsPending} waiting)` : ''}`},
]

const csvCell = (v) => {
  const s = String(v ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
function downloadCsv(rows, label) {
  const currencies = [...new Set(rows.flatMap((p) => Object.keys(p.feeCents)))].sort()
  const header = [
    'Practitioner', 'Code', 'Town', 'Referred', 'Practitioners referred', 'Went live', 'Pay Premium', 'Clients referred',
    'Clients who booked', 'Paid bookings', ...currencies.map((c) => `EnForma fees ${c}`), 'Premium revenue USD',
    'Rewards given', 'Rewards waiting', 'Practitioner id',
  ]
  const lines = rows.map((p) => [
    p.name, p.code, townLabel(p.town), p.referred, p.practitioners, p.live, p.paidPremium, p.clients, p.bookingClients,
    p.bookings, ...currencies.map((c) => ((p.feeCents[c] || 0) / 100).toFixed(2)), (p.premiumUsdCents / 100).toFixed(2),
    p.rewardsGiven, p.rewardsPending, p._id,
  ])
  const csv = [header, ...lines].map((l) => l.map(csvCell).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}))
  const a = document.createElement('a')
  a.href = url
  a.download = `enforma-referrals-${label}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const Tile = ({label, value, sub, small}) => (
  <div className='col-6 col-md-4 col-xl'>
    <div className='border rounded p-4 h-100'>
      <div className='text-muted fs-7 mb-1'>{label}</div>
      <div className={`${small ? 'fs-4' : 'fs-2'} fw-bolder`}>{value}</div>
      {sub && <div className='text-muted fs-8 mt-1'>{sub}</div>}
    </div>
  </div>
)

const Person = ({p}) =>
  p ? (
    <div className='d-flex align-items-center gap-3'>
      <div
        className='rounded-circle bg-light flex-shrink-0'
        style={{
          width: 36,
          height: 36,
          backgroundImage: p.profileImageUrl ? `url(${p.profileImageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div>
        <Link to={`/apps/agents/details-page/${p._id}/bookings`} className='text-dark fw-bold text-hover-primary'>
          {p.name || '(no name)'}
        </Link>
        <div className='text-muted fs-8'>
          {townLabel(p.town) || '—'} · <span className={p.member ? 'text-success' : ''}>{p.member ? 'Member' : 'Not listed'}</span>
        </div>
      </div>
    </div>
  ) : (
    <span className='text-muted'>—</span>
  )

function CodeCell({p, onSaved}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(p.code || '')
  const [error, setError] = useState('')
  const save = () =>
    axios
      .put(`${BASE_URL}/referrals/admin/code/${p._id}`, {code: value})
      .then(({data}) => {
        setEditing(false)
        setError('')
        onSaved(p._id, data.code)
      })
      .catch((e) => setError(e?.response?.data?.message || 'Could not save'))
  if (!editing)
    return (
      <button className='btn btn-link p-0 fw-bold' title='Change code' onClick={() => setEditing(true)}>
        {p.code || 'set code'}
      </button>
    )
  return (
    <div>
      <div className='d-flex gap-1'>
        <input
          className='form-control form-control-sm w-100px'
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          autoFocus
        />
        <button className='btn btn-sm btn-primary' onClick={save}>Save</button>
        <button className='btn btn-sm btn-light' onClick={() => setEditing(false)}>×</button>
      </div>
      {error && <div className='text-danger fs-8 mt-1'>{error}</div>}
    </div>
  )
}

function RewardRow({w, onDone}) {
  const [as, setAs] = useState(w.suggested)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const options = w.kind === 'practitioner_live' ? ['spotlight'] : w.paysInStore ? ['top_spot'] : ['premium_month', 'top_spot']
  const act = (what) => {
    setBusy(true)
    setError('')
    axios
      .post(`${BASE_URL}/referrals/admin/rewards/${w._id}/${what}`, what === 'give' ? {as} : {})
      .then(onDone)
      .catch((e) => setError(e?.response?.data?.message || 'Something went wrong'))
      .finally(() => setBusy(false))
  }
  const blocked = w.atLimit && as !== 'spotlight'
  return (
    <tr>
      <td><Person p={w.referrer} /></td>
      <td>
        {why(w)}
        <div className='text-muted fs-8'>Earned {date(w.createdAt)}</div>
      </td>
      <td>
        {options.length > 1 ? (
          <select className='form-select form-select-sm form-select-solid' value={as} onChange={(e) => setAs(e.target.value)}>
            {options.map((o) => (
              <option key={o} value={o}>{REWARDS[o]}</option>
            ))}
          </select>
        ) : (
          <div className='fw-bold'>{REWARDS[options[0]]}</div>
        )}
        <div className='text-muted fs-8 mt-1'>{HOW[as]}</div>
        {w.paysInStore && as !== 'spotlight' && (
          <div className='text-muted fs-8'>They pay through the App Store or Google Play, so a free month can’t stop their billing.</div>
        )}
        {blocked && <div className='text-danger fs-8'>Limit reached: {w.monthsUsed} reward months in the last 12 months.</div>}
        {error && <div className='text-danger fs-8'>{error}</div>}
      </td>
      <td className='text-end text-nowrap'>
        <button className='btn btn-sm btn-success me-2' disabled={busy || blocked} onClick={() => act('give')}>
          {as === 'premium_month' ? 'Give free month' : 'Given'}
        </button>
        <button className='btn btn-sm btn-light' disabled={busy} onClick={() => act('skip')}>Skip</button>
      </td>
    </tr>
  )
}

const Referrals = () => {
  const [range, setRange] = useState('all')
  const [town, setTown] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({key: 'referred', dir: -1})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)
  const [checking, setChecking] = useState('')

  const from = RANGES[range].from()
  const to = RANGES[range].to()

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError('')
    axios
      .get(`${BASE_URL}/referrals/admin/summary`, {params: {from: from || undefined, to: to || undefined}})
      .then(({data}) => alive && setData(data))
      .catch(() => alive && setError('Could not load referrals.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [from, to, tick])

  const reload = () => setTick((x) => x + 1)
  const checkNow = () => {
    setChecking('Checking…')
    axios
      .post(`${BASE_URL}/referrals/admin/check`)
      .then(({data}) => {
        setChecking(`${data.created} new reward${data.created === 1 ? '' : 's'}`)
        reload()
      })
      .catch(() => setChecking('Check failed'))
  }
  const codeSaved = (id, code) =>
    setData((d) => ({...d, referrers: d.referrers.map((p) => (p._id === id ? {...p, code} : p))}))

  const towns = useMemo(() => [...new Set((data?.referrers || []).map((p) => p.town).filter(Boolean))].sort(), [data])
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const col = COLUMNS.find((c) => c.key === sort.key)
    return (data?.referrers || [])
      .filter((p) => !town || p.town === town)
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q))
      .sort((a, b) => sort.dir * ((col ? col.get(a) - col.get(b) : 0) || (a.name || '').localeCompare(b.name || '')))
  }, [data, town, search, sort])

  const t = useMemo(() => {
    const sum = (f) => rows.reduce((s, p) => s + (f(p) || 0), 0)
    const fees = rows.reduce((acc, p) => {
      Object.entries(p.feeCents).forEach(([c, v]) => (acc[c] = (acc[c] || 0) + v))
      return acc
    }, {})
    return {
      referred: sum((p) => p.referred),
      practitioners: sum((p) => p.practitioners),
      live: sum((p) => p.live),
      paid: sum((p) => p.paidPremium),
      clients: sum((p) => p.clients),
      booked: sum((p) => p.bookingClients),
      fees,
      premium: sum((p) => p.premiumUsdCents),
    }
  }, [rows])

  const pending = (data?.rewards || []).filter((w) => w.status === 'pending' && (!town || w.referrer?.town === town))
  const decided = (data?.rewards || []).filter((w) => w.status !== 'pending' && (!town || w.referrer?.town === town))
  const sortBy = (key) => setSort((s) => ({key, dir: s.key === key ? -s.dir : -1}))
  const rules = data?.rules

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6 flex-wrap gap-3'>
        <div className='card-title m-0 flex-column align-items-start'>
          <h3 className='fw-bolder m-0'>Referrals</h3>
          <span className='text-muted fs-7 mt-1'>Practitioners and businesses who invite others</span>
        </div>
        <div className='card-toolbar gap-2 flex-wrap'>
          <select className='form-select form-select-solid w-225px' value={range} onChange={(e) => setRange(e.target.value)}>
            {Object.entries(RANGES).map(([k, r]) => (
              <option key={k} value={k}>{r.label}</option>
            ))}
          </select>
          <select className='form-select form-select-solid w-175px' value={town} onChange={(e) => setTown(e.target.value)}>
            <option value=''>All towns</option>
            {towns.map((x) => (
              <option key={x} value={x}>{townLabel(x)}</option>
            ))}
          </select>
          <button className='btn btn-light' onClick={checkNow} title='Rewards are checked every hour; this checks now'>
            {checking || 'Check for new rewards'}
          </button>
          <button className='btn btn-light-primary' disabled={!rows.length} onClick={() => downloadCsv(rows, range === 'all' ? 'all-time' : `${from}-to-${to}`)}>
            Export CSV
          </button>
        </div>
      </div>

      <div className='card-body pt-2'>
        {rules && (
          <div className='alert alert-secondary py-3 fs-7 text-gray-800'>
            Every practitioner and business has a code and a link (app.enformawellness.com/r/CODE). People are credited
            only when they type the code at sign-up, or open the link and then sign up (visiting a booking page doesn’t
            count). Rewards count for {rules.windowMonths} months after each person joins: a spotlight when an invited
            practitioner goes live, and a free month (or a top spot, for store payers) when they pay for Premium or for
            every {rules.clientsPerReward} invited clients with a paid booking. At most {rules.maxRewardMonthsPerYear}{' '}
            reward months per practitioner in 12 months. Invited practitioners get a {rules.referredTrialMonths}-month free
            trial once that store offer is switched on (until then, the standard trial). Nothing is given until you approve
            it below.
          </div>
        )}
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        {loading && !data && <div className='text-muted py-10 text-center'>Loading…</div>}

        {data && (
          <div style={{opacity: loading ? 0.5 : 1}}>
            <div className='row g-3 mb-8'>
              <Tile label='People referred' value={n(t.referred)} sub={`${n(t.practitioners)} practitioners · ${n(t.clients)} clients`} />
              <Tile label='Practitioners live' value={n(t.live)} />
              <Tile label='Paying for Premium' value={n(t.paid)} />
              <Tile label='Clients who booked' value={n(t.booked)} />
              <Tile small label='EnForma fees from referred clients' value={money(t.fees)} />
              <Tile small label='Premium revenue from referrals' value={usd(t.premium)} />
            </div>

            <h5 className='fw-bold mb-3'>Rewards to approve {pending.length ? `(${pending.length})` : ''}</h5>
            {!pending.length ? (
              <div className='text-muted fs-7 mb-8'>Nothing waiting.</div>
            ) : (
              <div className='table-responsive mb-8'>
                <table className='table table-row-dashed align-middle fs-7 gy-3'>
                  <thead>
                    <tr className='text-muted fw-bold text-uppercase fs-8'>
                      <th className='min-w-200px'>Practitioner</th>
                      <th className='min-w-250px'>Why</th>
                      <th className='min-w-250px'>Reward</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((w) => (
                      <RewardRow key={w._id} w={w} onDone={reload} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className='d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2'>
              <h5 className='fw-bold m-0'>Leaderboard</h5>
              <input
                className='form-control form-control-solid w-250px'
                placeholder='Search name or code'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className='table-responsive mb-8'>
              <table className='table table-row-dashed align-middle fs-7 gy-3'>
                <thead>
                  <tr className='text-muted fw-bold text-uppercase fs-8'>
                    <th className='min-w-200px'>Practitioner</th>
                    <th>Code</th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className='text-end text-nowrap' role='button' title={c.hint || `Sort by ${c.label}`} onClick={() => sortBy(c.key)}>
                        {c.label}
                        {sort.key === c.key ? (sort.dir < 0 ? ' ↓' : ' ↑') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p._id}>
                      <td><Person p={p} /></td>
                      <td><CodeCell p={p} onSaved={codeSaved} /></td>
                      {COLUMNS.map((c) => (
                        <td key={c.key} className='text-end text-nowrap'>
                          {c.show ? c.show(p) : c.get(p) ? n(c.get(p)) : '–'}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={COLUMNS.length + 2} className='text-center text-muted py-10'>
                        No referrals yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!!decided.length && (
              <>
                <h5 className='fw-bold mb-3'>Decided in the last 90 days</h5>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle fs-7 gy-2'>
                    <tbody>
                      {decided.map((w) => (
                        <tr key={w._id}>
                          <td><Person p={w.referrer} /></td>
                          <td>{why(w)}</td>
                          <td>{w.status === 'given' ? REWARDS[w.given] : <span className='text-muted'>Skipped</span>}</td>
                          <td className='text-muted text-end'>{date(w.decidedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Referrals
